import React, { useCallback, useId, useState } from "react";
import { History as HistoryIcon } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import { HistoryEvent, ResidentHistory as HistoryData, providerDocumentsApi } from "./providerDocumentsApi";

/**
 * History v1 (Isaiah Cotto, 2026-10-22): the resident's history and the provider's own work on them, read-only.
 *
 * Collapsed until opened (a native <details>, so it is a keyboard- and screen-reader-operable disclosure with no
 * script of its own); the first open loads it. Only provider-safe sources: CounselEAR visits, ATD changes (never the
 * reason written for them), referrals, and the caller's own private notes as dates. No PA / UMED / Finance work.
 */
export interface ResidentHistoryProps {
  facilityId: string;
  patientId: string;
}

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "time not recorded";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** A date-only value (YYYY-MM-DD) shown as that calendar day, never shifted by the viewer's time zone. */
export function dayWords(ymd: string | null): string {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "date not recorded";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

const EVIDENCE_WORDS: Record<string, string> = {
  AUDIOMETRIC_TEST: "hearing test",
  CLINICAL_VISIT_NON_TEST: "clinical visit",
  DISPENSE: "dispense",
  FOLLOW_UP: "follow-up",
  UNKNOWN_VISIT: "visit",
};

export function eventWords(e: HistoryEvent): { title: string; detail: string; time: string; dateTime: string } {
  switch (e.kind) {
    case "VISIT":
      return {
        title: `CounselEAR ${EVIDENCE_WORDS[e.evidence_class] || "visit"}`,
        detail: [e.encounter_type, e.provider ? `with ${e.provider}` : null].filter(Boolean).join(" · "),
        time: dayWords(e.date),
        dateTime: e.date || "",
      };
    case "ATD":
      return {
        title: `ATD ${e.from ? `${e.from} → ` : ""}${e.to}`,
        detail: e.source === "MONDAY_BASELINE" ? "as Monday last showed it" : e.by ? `by ${e.by}` : "by eMerge",
        time: e.date ? dayWords(e.date) : when(e.at),
        dateTime: e.date || e.at,
      };
    case "REFERRAL":
      return {
        title: "Audiology referral filed",
        detail: `by ${e.is_you ? "you" : e.by || "a provider"}${e.misfiled ? " · marked not this resident's" : ""}`,
        time: when(e.at),
        dateTime: e.at,
      };
    case "PRIVATE_NOTE":
    default:
      return { title: "You wrote a private note", detail: "", time: when(e.at), dateTime: e.at };
  }
}

type Load =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "ready"; data: HistoryData }
  | { state: "refused"; message: string }
  | { state: "error"; message: string };

export function ResidentHistory({ facilityId, patientId }: ResidentHistoryProps) {
  const ids = useId();
  const listId = `${ids}-list`;
  const [load, setLoad] = useState<Load>({ state: "idle" });

  const fetchHistory = useCallback(async () => {
    setLoad({ state: "loading" });
    try {
      const r = await providerDocumentsApi.history(facilityId, patientId);
      if (r.success && r.data && Array.isArray(r.data.events)) setLoad({ state: "ready", data: r.data });
      else if (r.success) setLoad({ state: "error", message: "The history could not be loaded." });
      else if (r.status === undefined && !r.code) setLoad({ state: "error", message: "Could not reach eMerge to load the history." });
      else if (r.status === 403) setLoad({ state: "refused", message: "The history is for the providers who work this resident and the office." });
      else if (r.status === 404) setLoad({ state: "refused", message: r.message || "This resident is not confirmed at this building." });
      else setLoad({ state: "error", message: r.message || "The history could not be loaded." });
    } catch {
      setLoad({ state: "error", message: "Could not reach eMerge to load the history." });
    }
  }, [facilityId, patientId]);

  const data = load.state === "ready" ? load.data : null;
  const notes: string[] = [];
  if (data) {
    if (data.sources.visits.reason === "NO_COUNSELEAR_ID") notes.push("This resident has no CounselEAR id, so CounselEAR visits cannot be shown.");
    if (data.sources.visits.reason === "NOT_IN_CARE" || data.access === "AUTHOR_ONLY") notes.push("You see only your own work: you no longer work this resident.");
    if (data.sources.tag_changes.reason === "NOT_RECORDED") notes.push("Tag changes are not shown: eMerge keeps the current tags, not their history.");
    const cut = Object.values(data.sources).some((s) => s.truncated);
    if (cut) notes.push(`Showing the newest ${data.limit_per_source} of each kind.`);
  }

  return (
    <details
      className="fw-private-notes"
      data-testid="resident-history"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && load.state === "idle") fetchHistory();
      }}
    >
      <summary className="fw-replacement-title" aria-controls={listId}>
        <HistoryIcon size={14} aria-hidden="true" /> History
      </summary>
      <div id={listId}>
        <p className="fw-muted">
          CounselEAR visits, ATD changes, referrals and your own private notes. Patient Advocate, UMED and Finance work is
          not shown here.
        </p>
        {load.state === "loading" && <p role="status" className="fw-muted">Loading history…</p>}
        {load.state === "refused" && <p className="fw-muted">{load.message}</p>}
        {load.state === "error" && (
          <div role="status" className="fw-error">
            <p>{load.message}</p>
            <Button variant="outline" type="button" onClick={fetchHistory}>Try again</Button>
          </div>
        )}
        {data && (
          <>
            {notes.map((n) => <p key={n} className="fw-muted">{n}</p>)}
            {data.events.length === 0 ? (
              <p className="fw-muted">Nothing recorded yet.</p>
            ) : (
              <ol className="fw-private-note-list" aria-label="History, newest first">
                {data.events.map((e) => {
                  const w = eventWords(e);
                  return (
                    <li key={e.id} className="fw-private-note">
                      <p><strong>{w.title}</strong></p>
                      <p className="fw-muted">
                        <time dateTime={w.dateTime}>{w.time}</time>
                        {w.detail ? ` · ${w.detail}` : ""}
                      </p>
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        )}
      </div>
    </details>
  );
}
export default ResidentHistory;
